/**
 * SSC header behaviour (blocks/_header-logo.liquid).
 *
 * 1. Context-aware logo:
 * If the page has a brand hero ([data-hero-brand], set by sections/hero.liquid when it contains the
 * SSC glass logo), the header logo stays hidden while the hero is under the header and fades in once
 * the hero's bottom edge passes the header's bottom edge. Pages without a brand hero keep the logo.
 * IntersectionObserver only (no scroll listeners); the observer's top margin is the header height.
 * While hidden, the 3D logo is paused (data-ssc-paused) and its link is inert.
 *
 * The same state (.is-past-hero) switches the header from transparent with difference-blended white
 * text over the hero to the normal white header (CSS in blocks/_header-logo.liquid).
 * While a mega menu panel is open over the hero, window.SSCHeader.setPanelOpen(true) shows the logo
 * in that white state too (.is-panel-open); on close the hero logic applies again.
 *
 * 2. Drawer-only menu: when the menu block's "Always show menu as burger" setting is on (overflow
 * list with minimum-items 999), keep Fabric's drawer layout even if its JS briefly picks 'menu',
 * so the inline desktop nav never flashes. The header is rendered with data-menu-style="drawer".
 */
(function () {
  'use strict';

  var observer = null;
  var headerResizeObserver = null;

  var pastHeroState = true;
  var panelOpen = false;

  /** The logo shows once past the hero, or while a mega menu panel is open (white header state) */
  function applyLogo(header) {
    var logo = header.querySelector('.header-logo [data-ssc-chrome-logo]');
    var shown = pastHeroState || panelOpen;
    var link = logo && logo.closest('a');
    if (link) link.inert = !shown;
    if (logo) {
      if (shown) logo.removeAttribute('data-ssc-paused');
      else logo.setAttribute('data-ssc-paused', '');
    }
  }

  function setPastHero(header, logo, pastHero) {
    pastHeroState = pastHero;
    header.classList.toggle('is-past-hero', pastHero);
    applyLogo(header);
  }

  /**
   * Public: header nav panels (assets/mega-panel.js) call this on open/close.
   * Adds .is-panel-open to the header (CSS shows the logo) and un-pauses the 3D logo.
   */
  function setPanelOpen(open) {
    var header = document.getElementById('header-component');
    if (!header) return;
    panelOpen = !!open;
    header.classList.toggle('is-panel-open', panelOpen);
    applyLogo(header);
  }

  function teardown() {
    if (observer) observer.disconnect();
    if (headerResizeObserver) headerResizeObserver.disconnect();
    observer = null;
    headerResizeObserver = null;
  }

  function setup() {
    teardown();

    var header = document.getElementById('header-component');
    if (!header) return;
    var logo = header.querySelector('.header-logo [data-ssc-chrome-logo]');
    var hero = document.querySelector('[data-hero-brand]');

    if (!hero || !('IntersectionObserver' in window)) {
      header.classList.remove('has-hero-brand');
      setPastHero(header, logo, true);
      return;
    }

    header.classList.add('has-hero-brand');
    // Start over the hero (logo hidden and paused); the observer's first callback sets the real state
    setPastHero(header, logo, false);

    var observedHeight = -1;
    function observe() {
      var height = Math.round(header.getBoundingClientRect().height);
      if (height === observedHeight) return;
      observedHeight = height;
      if (observer) observer.disconnect();
      observer = new IntersectionObserver(
        function (entries) {
          var entry = entries[entries.length - 1];
          setPastHero(header, logo, !entry.isIntersecting);
        },
        { rootMargin: '-' + height + 'px 0px 0px 0px', threshold: 0 }
      );
      observer.observe(hero);
    }

    observe();
    if ('ResizeObserver' in window) {
      headerResizeObserver = new ResizeObserver(observe);
      headerResizeObserver.observe(header);
    }
  }

  function refresh() {
    keepDrawerMenu();
    setup();
    if (window.SSCChromeLogo) window.SSCChromeLogo.init();
  }

  var menuStyleObserver = null;
  function keepDrawerMenu() {
    if (menuStyleObserver) menuStyleObserver.disconnect();
    var header = document.getElementById('header-component');
    if (!header || !header.querySelector('overflow-list[minimum-items="999"]')) return;
    function enforce() {
      if (header.dataset.menuStyle !== 'drawer') header.dataset.menuStyle = 'drawer';
    }
    enforce();
    menuStyleObserver = new MutationObserver(enforce);
    menuStyleObserver.observe(header, { attributes: true, attributeFilter: ['data-menu-style'] });
  }

  function init() {
    keepDrawerMenu();
    setup();
  }

  window.SSCHeader = { setPanelOpen: setPanelOpen };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', function () {
    // The unloaded section is still in the DOM during this event; re-check once it's gone
    setTimeout(refresh, 0);
  });
})();
